/**
 * Xreal Air VR PDP — prescription flow.
 *
 * Unlike the accessories template, the ATC button here does NOT add
 * to cart directly. It opens the prescription popup (the existing
 * rx-form Alpine component, wrapped inside this template) which
 * collects the customer's SPH/CYL/AXIS/PD and bundles the VR unit
 * with the selected RX lens variant (+ optional coating) at checkout.
 *
 * This file ships the template-specific custom elements:
 *   - <pdp-xreal-page>   root: boots state from the rendered JSON
 *   - <pdp-lens-options> renders rx-lens variant buttons with +plus_price
 *
 * Generic elements (title, price, gallery, sticky-cart, product-options,
 * add-to-cart…) come from pdp-core.
 */
import { PdpBaseComponent, PdpUtils } from 'pdp-core';

// ============================================================================
// <pdp-xreal-page> — orchestrator
// ============================================================================

class PdpXrealPage extends PdpBaseComponent {
  init() {
    this.loadInitialData();
    this.recomputeTotal();
  }

  setupListeners() {
    this.listen(this, 'pdp:change-variant', (e) => this.handleChangeVariant(e.detail));
    this.listen(this, 'pdp:change-lens-variant', (e) => this.handleChangeLensVariant(e.detail));
    this.listen(this, 'pdp:add-to-cart', () => this.handleAddToCart());

    // Whenever either the main variant or the lens variant changes,
    // refresh the single `totalPrice` field the price components listen to.
    this.onStateChange('selectedVariant', () => this.recomputeTotal());
    this.onStateChange('rxLensSelectedVariant', () => this.recomputeTotal());
  }

  recomputeTotal() {
    const main = this.state.get('selectedVariant');
    const lens = this.state.get('rxLensSelectedVariant');
    if (!main) return;

    const total = (main.price || 0) + (lens?.price || 0);
    const mainCompare = main.compare_at_price || main.price || 0;
    const lensCompare = lens?.compare_at_price || lens?.price || 0;
    const totalCompare = mainCompare + lensCompare;
    const hasDiscount = totalCompare > total;

    this.state.setState({
      totalPrice: total,
      totalCompareAtPrice: hasDiscount ? totalCompare : null,
    });
  }

  loadInitialData() {
    const productDataEl = this.querySelector('[data-pdp-product-data]');
    const lensDataEl = this.querySelector('[data-pdp-lens-product-data]');
    if (!productDataEl) {
      console.warn('[pdp-xreal] missing [data-pdp-product-data]');
      return;
    }

    try {
      const { product, productOptions, selectedVariant } = JSON.parse(productDataEl.textContent);
      const updates = {
        product,
        productOptions: productOptions || [],
        selectedVariant: selectedVariant || product.variants[0],
        images: PdpUtils.buildImages(product),
      };

      if (lensDataEl) {
        const lens = JSON.parse(lensDataEl.textContent);
        updates.rxLensProduct = lens.product;
        updates.rxLensProductOptions = lens.productOptions || [];
        updates.rxLensSelectedVariant =
          lens.selectedVariant || lens.product?.variants?.[0] || null;
      }

      this.state.setState(updates);
    } catch (e) {
      console.error('[pdp-xreal] failed to parse product data', e);
    }
  }

  handleChangeVariant({ optionName, value }) {
    const product = this.state.get('product');
    const selected = this.state.get('selectedVariant');
    if (!product || !selected) return;

    const option = this.state.get('productOptions').find((o) => o.name === optionName);
    if (!option) return;

    const newOptions = [...selected.options];
    newOptions[option.position - 1] = value;
    const next = PdpUtils.findVariantByOptions(product, newOptions);
    if (next) this.state.set('selectedVariant', next);
  }

  handleChangeLensVariant({ optionName, value }) {
    const lensProduct = this.state.get('rxLensProduct');
    const selected = this.state.get('rxLensSelectedVariant');
    if (!lensProduct || !selected) return;

    const option = this.state.get('rxLensProductOptions').find((o) => o.name === optionName);
    if (!option) return;

    const newOptions = [...selected.options];
    newOptions[option.position - 1] = value;
    const next = PdpUtils.findVariantByOptions(lensProduct, newOptions);
    if (next) this.state.set('rxLensSelectedVariant', next);
  }

  /**
   * RX template: clicking ATC must open the Alpine prescription popup,
   * not add to cart. The popup (rx-form) handles the bundled checkout.
   * We dispatch a bubbling window event so the Alpine wrapper can listen
   * via `@pdp:open-prescription.window`.
   */
  handleAddToCart() {
    window.dispatchEvent(new CustomEvent('pdp:open-prescription', {
      detail: {
        variant: this.state.get('selectedVariant'),
        lensVariant: this.state.get('rxLensSelectedVariant'),
      },
    }));
  }
}

// ============================================================================
// REGISTER (pdp-lens-options moved to pdp-core so mask-optics can use it too)
// ============================================================================

if (!customElements.get('pdp-xreal-page')) {
  customElements.define('pdp-xreal-page', PdpXrealPage);
}
